'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/app/auth-provider';
import { syncServerSessionForUser } from '@/lib/auth-session';

type StripeRole = 'basic' | 'premium' | 'business_plus' | null;

interface RoleData {
  stripeRole: StripeRole;
  hasWorkspaceAccess: boolean;
  loading: boolean;
  error: Error | null;
}

export const useStripeRole = () => {
  const { user } = useAuth();
  const searchParams = useSearchParams();
  const checkoutParam = searchParams.get('checkout');
  const [roleData, setRoleData] = useState<RoleData>({
    stripeRole: null,
    hasWorkspaceAccess: false,
    loading: true,
    error: null,
  });
  const resolvedUidRef = useRef<string | null>(null);

  // Per React best-practices §5.1: adjust state during render when a dependency
  // changes, not inside an effect. This prevents the one-frame gap where
  // roleLoading=false + stripeRole=null causes the gate to flash the paywall.
  if (user && user.uid !== resolvedUidRef.current && !roleData.loading) {
    setRoleData({ stripeRole: null, hasWorkspaceAccess: false, loading: true, error: null });
  }
  if (!user && resolvedUidRef.current !== null) {
    resolvedUidRef.current = null;
  }

  useEffect(() => {
    if (!user) {
      setRoleData({
        stripeRole: null,
        hasWorkspaceAccess: false,
        loading: false,
        error: null,
      });
      return;
    }

    let mounted = true;

    const fetchRole = async() => {
      try {
        const isCheckoutSuccess = checkoutParam === 'success';

        let tokenResult = await user.getIdTokenResult(isCheckoutSuccess);
        let role = tokenResult.claims.stripeRole as StripeRole;
        let performedForcedRefresh = isCheckoutSuccess;
        let hasWorkspaceAccess = false;

        if (!role && !isCheckoutSuccess) {
          await user.getIdToken(true);
          tokenResult = await user.getIdTokenResult();
          role = tokenResult.claims.stripeRole as StripeRole;
          performedForcedRefresh = true;
        }

        if (!role) {
          const idToken = await user.getIdToken();
          const accessResponse = await fetch('/api/auth/access', {
            headers: { Authorization: `Bearer ${idToken}` },
            cache: 'no-store',
          });
          if (accessResponse.ok) {
            const accessPayload = await accessResponse.json();
            hasWorkspaceAccess = Boolean(accessPayload?.hasWorkspaceAccess);
          }
        }

        if (performedForcedRefresh) {
          try {
            await syncServerSessionForUser(user);
          } catch (syncError) {
            console.error('Session sync failed after refreshing access claims:', syncError);
          }
        }

        if (mounted) {
          resolvedUidRef.current = user.uid;
          setRoleData({
            stripeRole: role || null,
            hasWorkspaceAccess,
            loading: false,
            error: null,
          });
        }
      } catch (err) {
        if (mounted) {
          resolvedUidRef.current = user.uid;
          setRoleData({
            stripeRole: null,
            hasWorkspaceAccess: false,
            loading: false,
            error: err instanceof Error ? err : new Error('Failed to fetch role'),
          });
        }
      }
    };

    fetchRole();

    return () => {
      mounted = false;
    };
  }, [user, checkoutParam]);

  // Firebase best practice - simple refresh function
  const refreshRole = async() => {
    if (!user) return;

    setRoleData(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Firebase best practice - force token refresh when explicitly requested
      await user.getIdToken(true);
      const tokenResult = await user.getIdTokenResult();
      const role = tokenResult.claims.stripeRole as StripeRole;
      let hasWorkspaceAccess = false;
      if (!role) {
        const accessResponse = await fetch('/api/auth/access', {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` },
          cache: 'no-store',
        });
        if (accessResponse.ok) {
          const accessPayload = await accessResponse.json();
          hasWorkspaceAccess = Boolean(accessPayload?.hasWorkspaceAccess);
        }
      }

      setRoleData({
        stripeRole: role || null,
        hasWorkspaceAccess,
        loading: false,
        error: null,
      });
    } catch (err) {
      setRoleData({
        stripeRole: null,
        hasWorkspaceAccess: false,
        loading: false,
        error: err instanceof Error ? err : new Error('Failed to refresh role'),
      });
    }
  };

  // Computed values
  const hasBasicAccess = roleData.stripeRole === 'basic'
    || roleData.stripeRole === 'premium'
    || roleData.stripeRole === 'business_plus'
    || roleData.hasWorkspaceAccess;
  const hasPremiumAccess = roleData.stripeRole === 'premium' || roleData.stripeRole === 'business_plus';
  const isLoggedIn = !!user;

  return {
    stripeRole: roleData.stripeRole,
    hasBasicAccess,
    hasPremiumAccess,
    isLoggedIn,
    loading: roleData.loading,
    error: roleData.error,
    refreshRole,
  };
};
